/* TKO — minimum order quantity (batch 3). Reads a `min-qty:N` product tag and enforces it in the
 * storefront: product page quantity starts at N with min N plus a note; cart page and cart drawer
 * disable checkout while any tagged product is under its minimum. No theme section is edited —
 * this file is included once from layout/theme.liquid. Vanilla JS, no dependencies.
 *
 * Stock overrides the minimum (plans/stock-overrides-minimum.md): for a product we stock, the
 * kit's `product.tko-stock` template says how many are on the shelf. A quantity at or under that
 * has no minimum and ships now; above it the whole quantity is made to order and the minimum
 * applies. Quantities are totalled per variant across the cart, the same arithmetic the app uses
 * when the order arrives. Without the template (or for an untracked product) nothing changes. */
(function () {
  var TAG = /^min-qty:(\d{1,4})$/i;
  var cache = {};
  /* The minimum AND the product's own variant ids. The ids are what let the page enforce on EVERY
   * form that adds this product — the main form and the sticky add-to-cart bar are two separate
   * `/cart/add` forms for the same variant on the live theme — while still refusing a form for some
   * other product, even one FOREIGN below does not name. */
  function productInfo(handle) {
    if (!handle) return Promise.resolve(null);
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p) return null;
        var min = null;
        var tags = !p.tags ? [] : Array.isArray(p.tags) ? p.tags : String(p.tags).split(",");
        for (var i = 0; i < tags.length; i++) {
          var m = TAG.exec(String(tags[i]).trim());
          if (m && Number(m[1]) >= 2) {
            min = Number(m[1]);
            break;
          }
        }
        var ids = [];
        var vs = p.variants || [];
        for (var j = 0; j < vs.length; j++)
          if (vs[j] && vs[j].id != null) ids.push(String(vs[j].id));
        // Stock only matters where there is a minimum to override, so only then is it fetched.
        return (min ? stockFor(handle) : Promise.resolve({})).then(function (stock) {
          return { min: min, variantIds: ids, stock: stock };
        });
      })
      .catch(function () {
        return null;
      });
    return cache[handle];
  }
  /* Variant id -> units on the shelf, for TRACKED variants only. The template writes `qty` only
   * when Shopify tracks the variant: on an untracked one Liquid's quantity is units sold. A store
   * without the template answers with its ordinary product page, which is not JSON, and the
   * minimum then applies exactly as before. */
  function stockFor(handle) {
    return fetch("/products/" + encodeURIComponent(handle) + "?view=tko-stock", {
      credentials: "same-origin",
    })
      .then(function (r) {
        return r.ok ? r.text() : "";
      })
      .then(function (text) {
        var out = {};
        var data = JSON.parse(text);
        var vs = (data && data.variants) || [];
        for (var i = 0; i < vs.length; i++) {
          var v = vs[i];
          if (v && v.tracked === true && typeof v.qty === "number") out[String(v.id)] = v.qty;
        }
        return out;
      })
      .catch(function () {
        return {};
      });
  }
  /* The stock that overrides the minimum for a variant: a positive shelf count, else null. */
  function shelfFor(info, variant) {
    var s = info && info.stock ? info.stock[String(variant)] : undefined;
    return typeof s === "number" && s > 0 ? s : null;
  }
  function note(text) {
    var el = document.createElement("div");
    el.className = "tko-min-qty-note";
    el.setAttribute("role", "note");
    el.style.cssText = "margin:.5rem 0;font-size:.9em;opacity:.85";
    el.textContent = text;
    return el;
  }
  /* ── product page ── */
  /* Containers whose `/cart/add` forms belong to a DIFFERENT product than the page's, plus the
   * places a quantity input is a CART LINE rather than a product-form field.
   *
   * This list is the fix for the 2026-09-03 defect. The old code resolved the minimum once from the
   * page's own handle and then wrote min/step/value onto EVERY `input[name="quantity"]` in the
   * document — including a related-product quick-add and, via a class-only selector with no form
   * ancestor, the cart drawer's line items. Viewing a hat tagged `min-qty:12` therefore forced an
   * unrelated jersey's quick-add to 12 and, when the drawer opened, rewrote an unrelated cart line
   * to 12 and fired a bubbling `change` that made the theme issue `/cart/change`. A shopper's cart
   * was altered without them touching it. */
  var FOREIGN = [
    "[data-section-type*='recommend']",
    ".product-recommendations",
    ".hdt-product-recommendations",
    ".related-products",
    ".complete-the-look",
    ".recently-viewed",
    ".upsell",
    "hdt-cart-drawer",
    ".hdt-cart-drawer",
    "#hdt-cart-drawer",
    "cart-drawer",
    ".cart-drawer",
    ".mini-cart",
    ".hdt-quick-view",
    ".quick-view",
    ".quickview",
  ].join(",");

  function isForeign(el) {
    try {
      return Boolean(el.closest(FOREIGN));
    } catch (e) {
      // A selector this browser cannot parse must not silently widen the scope back to the page.
      return true;
    }
  }

  /* The page's own add-to-cart form: the first `/cart/add` form that is not inside one of the
   * containers above. First, not "any" — a quick-add rendered before the main form would otherwise
   * be indistinguishable, and picking one form is what makes the minimum belong to one product. */
  function mainProductForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) if (!isForeign(forms[i])) return forms[i];
    return null;
  }

  /**
   * Does this input belong to `form`?
   *
   * `input.form` is the spec answer and it resolves BOTH ways an input can be associated: as a
   * descendant, and by the `form="<id>"` attribute. That attribute is not a corner case here — it
   * is what the live theme actually does (`snippets/quantity-selector.liquid`, read from the MAIN
   * theme 2026-09-03):
   *
   *     <input form="{{ product_form_id }}" class="hdt-quantity__input" name="quantity" ...>
   *
   * so the quantity widget is NOT inside the `/cart/add` form. That is why the pre-fix code needed
   * a class-only selector, and it is why scoping by `form.querySelectorAll(...)` or by a wrapper
   * class would have found nothing on the real storefront — the minimum would have silently stopped
   * applying, with every test still green.
   *
   * The `getAttribute` branch is a fallback for a DOM implementation that does not wire up `.form`.
   */
  function belongsTo(inp, form) {
    if (inp.form) return inp.form === form;
    var owner = inp.getAttribute("form");
    return owner ? owner === form.id : form.contains(inp);
  }

  /* The variant id for a form, resolved the SAME way the quantity input is: by form ASSOCIATION,
   * not by descent.
   *
   * The asymmetry this avoids is one this file already documents. The live theme attaches the
   * quantity input with `form="<id>"` rather than nesting it, which is why `belongsTo` exists. A
   * theme that did the same with the hidden `id` would make a descendant-only lookup find nothing,
   * so no form would match a variant, `productForms` would fall back to the first form, the sticky
   * bar would quietly stop being enforced — and every test would still pass. That is the exact
   * failure shape the comment above `belongsTo` warns about.
   *
   * On the live product page both forms nest it (checked 2026-09-03: one `name="id"` each, both
   * hidden inputs, both the same variant). Reading it by association keeps that from mattering.
   * `.value` reads the same on a `<select name="id">`, the other shape Shopify themes use. */
  function variantFor(form) {
    var fields = document.querySelectorAll('[name="id"]');
    for (var i = 0; i < fields.length; i++) {
      if (belongsTo(fields[i], form)) return String(fields[i].value || "");
    }
    return "";
  }

  /* Every `/cart/add` form that adds one of THIS product's variants.
   *
   * Plural, not `mainProductForm()`'s first match, because the live theme renders a second form for
   * the same product: the sticky add-to-cart bar (`hdt-sticky-atc__form`, verified on the real
   * product page 2026-09-03, same hidden `id` as the main form). Enforcing on the first form only
   * left that bar at min 1, so a shopper could add one of an item with a minimum of twelve.
   *
   * The variant id is what makes "every" safe, and it answers the reason `mainProductForm` took
   * only the first: a quick-add for another product is excluded because its variant is not ours,
   * whether or not FOREIGN happens to name its container. FOREIGN still applies as
   * defence-in-depth, and a form with no readable variant is skipped rather than guessed at. */
  function productForms(variantIds) {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var out = [];
    if (variantIds && variantIds.length) {
      for (var i = 0; i < forms.length; i++) {
        var form = forms[i];
        if (isForeign(form)) continue;
        var variant = variantFor(form);
        if (!variant || variantIds.indexOf(variant) === -1) continue;
        out.push(form);
      }
      if (out.length) return out;
    }
    // No usable variant ids (or no form carried a matching one): fall back to the first non-foreign
    // form. `/products/<handle>.js` always returns `variants`, so this is the defensive path — and
    // enforcing on one form beats the minimum silently not applying at all.
    var first = mainProductForm();
    return first ? [first] : [];
  }

  /**
   * Quantity inputs that belong to the page's product — a POSITIVE test, not a deny-list.
   *
   * An input qualifies when its associated form is a `/cart/add` form that is not foreign. That
   * excludes the cart drawer by the drawer's OWN action rather than by guessing its class name:
   * the live theme's drawer posts to `routes.cart_url` (`/cart`), never `/cart/add`. `FOREIGN`
   * remains as defence-in-depth for recommendation and quick-view forms, which ARE `/cart/add`
   * forms for other products.
   */
  function productInputs(variantIds) {
    var forms = productForms(variantIds);
    if (!forms.length) return [];
    var all = document.querySelectorAll('input[name="quantity"]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var inp = all[i];
      if (isForeign(inp)) continue;
      for (var f = 0; f < forms.length; f++) {
        if (belongsTo(inp, forms[f])) {
          out.push(inp);
          break;
        }
      }
    }
    return out;
  }

  /* The add-to-cart buttons of a form, nested or attached with `form="<id>"`. */
  function addButtons(form) {
    var out = [];
    var sel = 'button[type="submit"], button[name="add"], input[type="submit"]';
    form.querySelectorAll(sel).forEach(function (b) {
      out.push(b);
    });
    if (form.id)
      document.querySelectorAll(sel).forEach(function (b) {
        if (out.indexOf(b) === -1 && b.getAttribute("form") === form.id) out.push(b);
      });
    return out;
  }
  function setGap(form, host, text) {
    addButtons(form).forEach(function (b) {
      if (text) {
        b.disabled = true;
        b.dataset.tkoStockGap = "1";
      } else if (b.dataset.tkoStockGap) {
        b.disabled = false;
        delete b.dataset.tkoStockGap;
      }
    });
    var msg = host && host.querySelector(".tko-stock-gap");
    if (text && host) {
      if (!msg) {
        msg = note("");
        msg.className = "tko-stock-gap";
        msg.style.cssText += ";color:#b00020;font-weight:600";
        host.appendChild(msg);
      }
      if (msg.textContent !== text) msg.textContent = text;
    } else if (msg) msg.remove();
  }
  /* A product we stock with stock on the shelf: any quantity up to the shelf ships now; above it
   * the whole quantity is made to order and must reach the minimum. Between the two, adding is
   * refused with both choices named. The quantity is never snapped up to the minimum: that would
   * silently multiply an order. */
  function applyStock(inp, form, shelf, n) {
    inp.min = "1";
    var host =
      inp.closest(".hdt-quantity, .hdt-product-form__quantity, .product-form__quantity") ||
      inp.parentElement;
    if (host && !host.querySelector(".tko-min-qty-note"))
      host.appendChild(note(shelf + " in stock — ships now"));
    function check() {
      var q = Number(inp.value) || 0;
      var gap = q > shelf && q < n;
      setGap(
        form,
        host,
        gap
          ? "Only " +
              shelf +
              " in stock. Order up to " +
              shelf +
              " now, or " +
              n +
              " or more made to order."
          : "",
      );
    }
    inp.addEventListener("change", check);
    inp.addEventListener("input", check);
    check();
  }
  function formOf(inp, forms) {
    for (var i = 0; i < forms.length; i++) if (belongsTo(inp, forms[i])) return forms[i];
    return null;
  }

  function productPage() {
    var m = /\/products\/([^/?#]+)/.exec(location.pathname);
    if (!m) return;
    productInfo(decodeURIComponent(m[1])).then(function (info) {
      if (!info || !info.min) return;
      var n = info.min;
      // Collected AFTER the fetch: the variant ids decide which forms count, and the observer
      // re-runs this when the theme injects more of the page.
      var forms = productForms(info.variantIds);
      var inputs = productInputs(info.variantIds);
      inputs.forEach(function (inp) {
        if (inp.dataset.tkoMinQty) return;
        inp.dataset.tkoMinQty = String(n);
        var form = formOf(inp, forms);
        var shelf = form ? shelfFor(info, variantFor(form)) : null;
        if (shelf != null) {
          applyStock(inp, form, shelf, n);
          return;
        }
        /* A minimum is a FLOOR, not a pack size (owner 2026-09-03): 13 is a valid order, so set
         * `min` only and leave `step` alone — a step of n would snap 13 up to 24. */
        inp.min = String(n);
        if (Number(inp.value) < n) {
          inp.value = String(n);
          inp.dispatchEvent(new Event("change", { bubbles: true }));
        }
        inp.addEventListener("change", function () {
          if ((Number(inp.value) || 0) < n) inp.value = String(n);
        });
        var host =
          inp.closest(".hdt-quantity, .hdt-product-form__quantity, .product-form__quantity") ||
          inp.parentElement;
        if (host && !host.querySelector(".tko-min-qty-note"))
          host.appendChild(note("Minimum order " + n + " — you can order more."));
      });
    });
  }
  /* ── cart page + drawer ── */
  var checking = false;
  function cartCheck() {
    if (checking) return;
    checking = true;
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        // Totalled per variant: two lines of one product with different personalisation are one
        // order of that product, for the minimum and for the shelf alike.
        var byVariant = {};
        var order = [];
        ((cart && cart.items) || []).forEach(function (it) {
          var k = String(it.variant_id != null ? it.variant_id : it.key);
          if (!byVariant[k]) {
            byVariant[k] = { handle: it.handle, title: it.product_title, variant: k, have: 0 };
            order.push(k);
          }
          byVariant[k].have += Number(it.quantity) || 0;
        });
        return Promise.all(
          order.map(function (k) {
            var g = byVariant[k];
            return productInfo(g.handle).then(function (info) {
              var n = info && info.min;
              if (!n) return null;
              var shelf = shelfFor(info, g.variant);
              if (shelf != null && g.have <= shelf) return null;
              if (g.have >= n) return null;
              return { title: g.title, need: n, have: g.have, shelf: shelf };
            });
          }),
        );
      })
      .then(function (res) {
        var bad = res.filter(Boolean);
        document
          .querySelectorAll('button[name="checkout"], input[name="checkout"]')
          .forEach(function (btn) {
            var wrap = btn.parentElement;
            if (!wrap) return;
            var msg = wrap.querySelector(".tko-min-qty-block");
            if (bad.length) {
              btn.disabled = true;
              btn.setAttribute("aria-disabled", "true");
              btn.dataset.tkoBlocked = "1";
              var text = bad
                .map(function (b) {
                  return b.shelf != null
                    ? b.title +
                        ": only " +
                        b.shelf +
                        " in stock — order up to " +
                        b.shelf +
                        " now, or " +
                        b.need +
                        " or more made to order"
                    : b.title + ": minimum " + b.need + ", you have " + b.have;
                })
                .join(" · ");
              if (!msg) {
                msg = note("");
                msg.className = "tko-min-qty-block";
                msg.style.cssText += ";color:#b00020;font-weight:600";
                wrap.insertBefore(msg, btn);
              }
              // Idempotent: writing textContent is a childList mutation, and an unconditional write
              // would retrigger any observer watching the body.
              var full = "Below the minimum order — " + text;
              if (msg.textContent !== full) msg.textContent = full;
            } else if (btn.dataset.tkoBlocked) {
              btn.disabled = false;
              btn.removeAttribute("aria-disabled");
              delete btn.dataset.tkoBlocked;
              if (msg) msg.remove();
            }
          });
      })
      .catch(function () {})
      .then(function () {
        checking = false;
      });
  }
  /* Re-check after any cart mutation the theme makes.
   *
   * This stays GLOBAL on purpose, and it is the one part of the 2026-09-03 scoping fix that was
   * asked for and deliberately not done. The cart DRAWER opens on any page — collection, search,
   * home — and `cartCheck` is what disables checkout while a tagged line is under its minimum.
   * Scoping this to product and cart pages would silently drop that block everywhere else, which
   * trades a real enforcement hole for tidiness.
   *
   * It is also not the defect: this wrapper only ever calls `cartCheck`, which touches checkout
   * buttons and its own message node. It never writes to a quantity input, so it was not a route by
   * which one product's minimum reached another's. */
  var _fetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var p = _fetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url))
      p.then(
        function () {
          setTimeout(cartCheck, 150);
        },
        function () {},
      );
    return p;
  };
  // The fetch wrapper above already re-checks the cart after every cart mutation. The observer only
  // re-applies the product-page minimum when the theme re-renders the form — it must NEVER call
  // cartCheck, whose own DOM writes would retrigger it (a /cart.js poll every 200ms while blocked).
  var obs = new MutationObserver(function () {
    clearTimeout(obs._t);
    obs._t = setTimeout(productPage, 200);
  });
  function start() {
    productPage();
    cartCheck();
    // Only on a product page. The observer exists to re-apply the product-page minimum when the
    // theme re-renders the form, and `productPage` returns immediately anywhere else — so off a
    // product page this was a permanent body-wide subtree observer doing nothing on every
    // storefront page. Not the defect, but no reason to run.
    if (/\/products\//.test(location.pathname))
      obs.observe(document.body, { childList: true, subtree: true });
  }
  window.addEventListener("pageshow", cartCheck);
  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) cartCheck();
  });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
